import * as React from "react"
import { Interval, type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { type DigitsLocation } from "@digits-shared/components/Router/DigitsLocation"
import {
  type DigitsRoute,
  type Routes,
  type StaticRoutes,
} from "@digits-shared/components/Router/DigitsRoute"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import {
  generatePathFromCurrentRoute,
  type RouteForPathGeneration,
} from "@digits-shared/components/Router/pathGeneration"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import { useReadEmployeeFeature } from "src/frontend/hooks/useEmployeeFeatures"
import type FrontendSession from "src/frontend/session"
import { AllTimeContextProvider, useAllTimeModeProvider } from "src/shared/allTime/AllTimeContext"

/**
 * Context Consumer and Provider for Interval Origin
 */
export const TimeContext = React.createContext<IntervalOrigin>(
  dateTimeHelper.defaultIntervalOrigin()
)

interface TimeContextURLProviderProps {
  name?: string
  routes: Routes<StaticRoutes>
  children?: React.ReactNode
}

/**
 * Self contained React Component that determines and sets the interval origin based on the current
 * route.
 */
export const TimeContextURLProvider: React.FC<TimeContextURLProviderProps> = ({
  name,
  children,
  routes,
}) => {
  const { hasAccessToAspect, currentLegalEntityId } = useSession<FrontendSession>()
  const quarterlyDashboard = hasAccessToAspect?.(AspectCode.QuarterlyDashboard)
  const { location } = useRouter()
  const { queryParams } = location

  // props.match is not populated with time params since this is loaded always via the `children`
  // prop on the SessionRoute above. So we need to build one.
  // e.g. route /foo/:bar/:baz, path => /foo/forks/spoons => { bar: "forks", baz: "spoons" }
  const route = routes[location.name] as DigitsRoute

  const locationParams = React.useMemo(
    () => route?.getParametersFromPath(location.pathname) || {},
    [route, location.pathname]
  )

  // Deconstructing the time params because locationParams & queryParams will be a new object when any other param is added,
  // we only want to recreate the intervalOrigin when the time param changes.

  const { year, index, interval, intervalCount } = React.useMemo(
    () => ({
      year: locationParams.year || queryParams.year,
      index: locationParams.index || queryParams.index,
      interval: locationParams.interval || queryParams.interval,
      intervalCount: locationParams.intervalCount || queryParams.intervalCount,
    }),
    [locationParams, queryParams]
  )

  const routeTimeOptions = route?.config.timeParameterOptions

  // Parse interval for all-time mode
  const parsedInterval = interval
    ? Interval[(interval.charAt(0).toUpperCase() + interval.slice(1)) as keyof typeof Interval]
    : routeTimeOptions?.interval

  // Feature flag - only enable all-time functionality for employees
  const allTimeEnabled = !!useReadEmployeeFeature("allTimeDateRangeEnabled")

  // All-time mode state (centralized in allTime module)
  const {
    contextValue: allTimeModeContextValue,
    allTimeIntervalCount,
    isAllTimeUrl,
  } = useAllTimeModeProvider({
    enabled: allTimeEnabled,
    urlParams: { year, index },
    routeHasTimeOptions: !!routeTimeOptions,
    interval: parsedInterval,
  })

  // Ref to preserve intervalOrigin when navigating to routes without time tokens
  const prevOriginRef = React.useRef<IntervalOrigin>(undefined)
  // Track legal entity to reset prevOriginRef when switching entities while in all-time mode
  const prevLegalEntityIdRef = React.useRef<string | undefined>(currentLegalEntityId)

  // Reset prevOriginRef when legal entity changes while on all-time URL to avoid showing
  // stale data (e.g., previous LE's all-time range while new LE's data is loading).
  // Only reset for all-time URLs - for regular URLs, preserving prevOriginRef is needed
  // for proper interval handling when navigating from routes without time tokens.
  if (currentLegalEntityId !== prevLegalEntityIdRef.current) {
    if (isAllTimeUrl) {
      prevOriginRef.current = undefined
    }
    prevLegalEntityIdRef.current = currentLegalEntityId
  }

  // Compute intervalOrigin
  const intervalOrigin = React.useMemo(() => {
    // Handle all-time URL pattern: use computed allTimeIntervalCount
    if (isAllTimeUrl && routeTimeOptions && parsedInterval && allTimeIntervalCount !== undefined) {
      const todayOrigin = dateTimeHelper.todayIntervalOrigin(parsedInterval)
      const allTimeOrigin = { ...todayOrigin, intervalCount: allTimeIntervalCount }
      prevOriginRef.current = allTimeOrigin
      return allTimeOrigin
    }

    // Still loading allTimeIntervalCount or on all-time URL without data yet
    if (isAllTimeUrl && routeTimeOptions && prevOriginRef.current) {
      return prevOriginRef.current
    }

    // Route without time tokens - preserve previous origin
    if (!routeTimeOptions && prevOriginRef.current) {
      return prevOriginRef.current
    }

    // Regular route with time tokens - compute normally
    const defaultOrigin = dateTimeHelper.defaultIntervalOriginFromRoute(
      routeTimeOptions?.defaultIntervalOrigin,
      routeTimeOptions?.interval
    )

    const quarterlyOrigin = dateTimeHelper.changeIntervalInOrigin(
      defaultOrigin,
      Interval.Quarter,
      undefined
    )

    const newInterval = dateTimeHelper.intervalOriginFromRoute(
      {
        year,
        index,
        interval,
        intervalCount,
      },
      quarterlyDashboard ? quarterlyOrigin : defaultOrigin,
      routeTimeOptions
    )
    prevOriginRef.current = newInterval
    return newInterval
  }, [
    routeTimeOptions,
    quarterlyDashboard,
    year,
    index,
    interval,
    intervalCount,
    parsedInterval,
    isAllTimeUrl,
    allTimeIntervalCount,
  ])

  // Don't redirect for all-time URLs - they're valid
  // Check to see if current route is a path without or invalid time parameters. If so
  // redirect to the same route with the time tokens correctly in the path
  const redirectPath = isAllTimeUrl
    ? undefined
    : getRedirectPathIfInvalidTimeToken(intervalOrigin, location, routes, route, locationParams)

  if (redirectPath)
    return <LoggedRedirect name="TimeContextURLProvider-invalidTimeToken" to={redirectPath} />

  return (
    <AllTimeContextProvider value={allTimeModeContextValue}>
      <TimeContext.Provider value={intervalOrigin}>{children}</TimeContext.Provider>
    </AllTimeContextProvider>
  )
}

function getRedirectPathIfInvalidTimeToken(
  intervalOrigin: IntervalOrigin,
  location: DigitsLocation,
  routes: Routes<StaticRoutes>,
  route: DigitsRoute,
  params: Record<string, string>
) {
  if (!route?.config.timeParameterOptions) return undefined

  const routeConfigs = Object.values(routes) as RouteForPathGeneration[]
  const currentRoute =
    routeConfigs.find((config) => config.name === location.name) ?? routeConfigs[0]
  if (!currentRoute) return undefined

  const correctFullPathname = generatePathFromCurrentRoute(route as RouteForPathGeneration, {
    location,
    currentRoute,
    parameters: intervalOrigin,
    includedQueryParams: "includeAllQueryParams",
  })

  if (!dateTimeHelper.areRouteAndIntervalOriginEqual(params, intervalOrigin, true)) {
    return correctFullPathname
  }

  return undefined
}
